import React, { useEffect, useLayoutEffect, useCallback, useMemo, useState, useRef } from 'react';
import { Dropdown, Icon } from 'semantic-ui-react';
import LanguageSelector from './RightSection/LanguageSelector';
import UserMenu from './RightSection/UserMenu';
import { SignInLink, CreateAccountLink } from './RightSection/AuthLinks';
import OverflowMenu from './RightSection/OverflowMenu';
import NotesToggle from './RightSection/NotesToggle';
import AIToggle from './RightSection/AIToggle';
import HelpMenu from './RightSection/HelpMenu';
import { ThemeDropdown } from '../../themes';

// When the row runs out of room, controls fold into the ☰ menu in this order and come
// back in reverse as space returns. "Sign in" and the avatar menu never fold.
const FOLD_ORDER = ['theme', 'language', 'notes', 'create', 'zoom', 'help'];

// Space in front of each control in the row: tool controls sit 4px apart, the account
// group keeps 12px before the language globe and 10px between "Sign in" and "or".
const GAP_BEFORE = { theme: 4, zoom: 4, notes: 4, help: 4, trigger: 4, create: 10, language: 12 };
const DEFAULT_TRIGGER_WIDTH = 44;
const FIT_SLACK = 4;

const RightSection = React.memo(({
  show_ai_chat,
  user,
  userMenu,
  userMenuChange,
  zoomValue = 1,
  onZoomMenuChange,
  visible,
  setVisible,
  aiVisible,
  setAiVisible,
  action_name,
  embed_mode,
  show_help,
  help_l,
  is_user_signed_in,
  setTriedToSave,
  onMinWidthChange
}) => {
  const [folded, setFolded] = useState([]);
  const rootRef = useRef(null);
  const fixedRef = useRef(null);
  const triggerRef = useRef(null);
  const unitRefs = useRef({});
  const unitWidths = useRef({});
  const lastMinWidth = useRef(null);
  const [sparkActive, setSparkActive] = useState(false);
  const [isShowAll, setIsShowAll] = useState(false);
  const zoomDisplayRef = useRef(null);

  const zoomOptions = useMemo(() => [
    {
      key: 'show-all',
      text: window.l?.show_all || 'Show All',
      value: 'show-all'
    },
    { key: '75', text: '75%', value: 0.75 },
    { key: '100', text: '100%', value: 1 },
    { key: '150', text: '150%', value: 1.5 }
  ], []);

  // Calculate the display value for the dropdown
  const dropdownValue = useMemo(() => {
    // If we're in "show all" mode, display that
    if (isShowAll) {
      console.log('dropdownValue returning show-all');
      return 'show-all';
    }

    // Check if current zoomValue matches one of our predefined options
    const matchedOption = zoomOptions.find(opt => opt.value === zoomValue);
    if (matchedOption) {
      console.log('dropdownValue returning matched:', matchedOption.value);
      return matchedOption.value;
    }

    // Otherwise, default to 100% if no match
    console.log('dropdownValue returning default 1, zoomValue was:', zoomValue);
    return 1;
  }, [isShowAll, zoomValue, zoomOptions]);

  const handleZoomChange = useCallback((e, { value }) => {
    console.log('handleZoomChange - value:', value);
    if (typeof onZoomMenuChange !== 'function') return;

    if (value === 'show-all') {
      // Call the show-all function on window if it exists
      com.clientside.main.Html5Bootstrap.jsCall('show-all', null, null);
      // Set local state to show "show-all" in dropdown
      setIsShowAll(true);
      console.log('Set isShowAll to true');
    } else {
      // Clear show-all state and update zoom value
      setIsShowAll(false);
      onZoomMenuChange(e, { value });
      console.log('Set isShowAll to false, zoomValue:', value);
    }

    // Trigger spark animation
    setSparkActive(true);
    setTimeout(() => setSparkActive(false), 600);
  }, [onZoomMenuChange]);

  const handleNotesToggle = useCallback((e) => {
    if (aiVisible) {
      setAiVisible(false);
      setVisible(true);
    } else if (visible) {
      setVisible(false);
    } else {
      setVisible(true);
    }
  }, [visible, setVisible, aiVisible, setAiVisible]);

  const handleAIToggle = useCallback((e) => {
    if (visible) {
      setVisible(false);
    }
    setAiVisible(!aiVisible);
    if (aiVisible){
      spark.init();
    }
  }, [aiVisible, setAiVisible, visible, setVisible]);

  // Expose setZoom method on window for external access
  useEffect(() => {
    window.setZoom = (value) => {
      if (typeof onZoomMenuChange !== 'function') {
        console.warn('onZoomMenuChange is not available');
        return;
      }

      let zoomValue;
      if (value === 'show-all') {
        zoomValue = 'show-all';
      } else {
        // Clamp value between 0.1 and 5
        zoomValue = Math.max(0.1, Math.min(5, value));
      }

      onZoomMenuChange({}, { value: zoomValue });

      // Trigger spark animation
      setSparkActive(true);
      setTimeout(() => setSparkActive(false), 600);
    };

    // Cleanup function to remove the method when component unmounts
    return () => {
      delete window.setZoom;
    };
  }, [onZoomMenuChange]);

  const styles = (
    <>
      <style>{`
        .right-section-btn {
          padding: 4px 8px !important;
          border-radius: var(--radius) !important;
          background: transparent !important;
          color: var(--primary) !important;
          height: 36px !important;
          display: flex !important;
          align-items: center !important;
        }

        .right-section-btn:not(.no-hover):hover {
          background-color: var(--primary) !important;
          color: var(--primary-foreground) !important;
        }

        .right-section-btn.active,
        .right-section-btn.toggle.active,
        .ui.toggle.button.active.right-section-btn {
          background: var(--chart-2) !important;
          color: var(--primary-foreground) !important;
        }

        .zoom-dropdown {
          background: var(--accent) !important;
          color: var(--accent-foreground) !important;
          border: 1px solid var(--sidebar-border) !important;
          border-radius: var(--radius) !important;
          min-height: 32px !important;
          max-height: 32px !important;
          min-width: 70px !important;
          max-width: 70px !important;
          transition: all 0.3s ease !important;
          font-size: 12px !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 24px 0 8px !important;
          position: relative !important;
        }

        .zoom-dropdown:hover {
          background-color: var(--sidebar-accent) !important;
        }

        .zoom-dropdown.active {
          animation: zoomSpark 0.6s ease-out;
        }

        .zoom-dropdown .text {
          color: var(--accent-foreground) !important;
          font-size: 12px !important;
          flex: 1 !important;
        }

        .zoom-dropdown i.icon {
          color: var(--accent-foreground) !important;
          opacity: 0.7 !important;
          font-size: 12px !important;
          margin: 0 !important;
          position: absolute !important;
          right: 4px !important;
          top: 50% !important;
          transform: translateY(-50%) !important;
          pointer-events: none !important;
        }

        .zoom-dropdown:hover i.icon {
          opacity: 1 !important;
        }

        .zoom-dropdown .menu {
          background: var(--popover) !important;
          border: 1px solid var(--border) !important;
          box-shadow: var(--shadow-lg) !important;
          min-width: 100px !important;
        }

        .zoom-dropdown .menu .item {
          color: var(--popover-foreground) !important;
          background: transparent !important;
          padding: 6px 10px !important;
          font-size: 12px !important;
        }


        .zoom-dropdown .menu .item:hover {
          background: var(--accent) !important;
        }

        .zoom-dropdown .menu .item.selected {
          background: var(--sidebar-accent) !important;
          font-weight: 600 !important;
        }

        @keyframes zoomSpark {
          0% {
            transform: scale(1);
            box-shadow: 0 0 0 var(--ring);
          }
          25% {
            transform: scale(1.05);
            box-shadow: 0 0 20px var(--ring), 0 0 40px var(--chart-4);
            border-color: var(--ring) !important;
          }
          50% {
            transform: scale(1.03);
            box-shadow: 0 0 30px var(--ring), 0 0 60px var(--chart-4);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 0 0 var(--ring);
          }
        }

      `}</style>

    </>
  );

  const userMenuElement = useMemo(() => (
    user && (
      <UserMenu
        key={user.id}
        userMenu={userMenu}
        user={user}
        userMenuChange={userMenuChange}
      />
    )
  ), [user, userMenu, userMenuChange]);

  const showAuth = !user && !embed_mode;
  const showNotes = action_name === 'show' || action_name === 'lab';
  const currentLanguage = document.documentElement.lang || 'en';

  // The controls present in this mode, in fold order
  const present = FOLD_ORDER.filter((key) => {
    if (key === 'notes') return showNotes;
    if (key === 'create') return showAuth;
    if (key === 'help') return !!show_help;
    return true;
  });
  const isFolded = (key) => folded.indexOf(key) !== -1;
  const visibleFolded = folded.filter((key) => present.indexOf(key) !== -1);

  // Every control, the ☰ and the never-folding span are observed as they attach, so a
  // width change without a re-render (a font or icon finishing loading, a remount) re-fits.
  const observerRef = useRef(null);
  const observedRef = useRef(new Map());
  const observeNode = (slot, el) => {
    const previous = observedRef.current.get(slot);
    if (previous === el) return;
    if (previous && observerRef.current) observerRef.current.unobserve(previous);
    if (el) {
      observedRef.current.set(slot, el);
      if (observerRef.current) observerRef.current.observe(el);
    } else {
      observedRef.current.delete(slot);
    }
  };

  const setUnitRef = (key) => (el) => {
    if (el) unitRefs.current[key] = el;
    else delete unitRefs.current[key];
    observeNode(key, el);
  };
  const setFixedRef = (el) => {
    fixedRef.current = el;
    observeNode('fixed', el);
  };
  const setTriggerRef = (el) => {
    triggerRef.current = el;
    observeNode('trigger', el);
  };

  // Measure what fits: the room the bar gives this section against the measured widths
  // of its controls (cached while a control is folded and out of the layout).
  const computeFit = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const available = root.clientWidth;
    if (!available) return;

    present.forEach((key) => {
      const el = unitRefs.current[key];
      if (el && el.offsetParent !== null) {
        unitWidths.current[key] = el.getBoundingClientRect().width + GAP_BEFORE[key];
      }
    });
    const fixedWidth = fixedRef.current ? fixedRef.current.getBoundingClientRect().width : 0;
    const triggerEl = triggerRef.current;
    const triggerWidth = triggerEl && triggerEl.offsetParent !== null
      ? triggerEl.getBoundingClientRect().width + GAP_BEFORE.trigger
      : DEFAULT_TRIGGER_WIDTH;

    const widthWith = (foldedKeys) => present.reduce((total, key) => (
      foldedKeys.indexOf(key) !== -1 ? total : total + (unitWidths.current[key] || 0)
    ), fixedWidth + (foldedKeys.length > 0 ? triggerWidth : 0) + FIT_SLACK);

    const next = [];
    for (let i = 0; i < present.length; i += 1) {
      if (widthWith(next) <= available) break;
      next.push(present[i]);
    }

    setFolded((prev) => (prev.join(',') === next.join(',') ? prev : next));

    // The bar must leave room at least for the controls that never fold and the ☰
    const minWidth = Math.ceil(fixedWidth + triggerWidth + FIT_SLACK);
    if (onMinWidthChange && lastMinWidth.current !== minWidth) {
      lastMinWidth.current = minWidth;
      onMinWidthChange(minWidth);
    }
  }, [present.join(','), onMinWidthChange]);

  const computeFitRef = useRef(computeFit);
  computeFitRef.current = computeFit;

  useLayoutEffect(() => {
    computeFit();
  });

  useEffect(() => {
    const handleResize = () => computeFitRef.current();
    window.addEventListener('resize', handleResize);
    let observer = null;
    if (typeof ResizeObserver !== 'undefined' && rootRef.current) {
      observer = new ResizeObserver(handleResize);
      observerRef.current = observer;
      observer.observe(rootRef.current);
      observedRef.current.forEach((el) => observer.observe(el));
    }
    return () => {
      window.removeEventListener('resize', handleResize);
      if (observer) observer.disconnect();
      observerRef.current = null;
    };
  }, []);

  // A folded control leaves the layout but stays mounted, so its ids and state survive
  const unitStyle = (key) => ({
    display: isFolded(key) ? 'none' : 'flex',
    alignItems: 'center',
    flexShrink: 0
  });

  const panelOpen = isFolded('notes') && (visible || (show_ai_chat && aiVisible));

  return (
    <div
      ref={rootRef}
      className="right-section-root"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
        minWidth: 0,
        margin: 0,
        marginTop: '2px',
        paddingRight: 0,
        paddingTop: '2px',
        paddingBottom: '0px'
      }}
    >
      {styles}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        flexShrink: 0,
        whiteSpace: 'nowrap'
      }}>
        {/* Theme Switcher - Compact dropdown with icon */}
        <span ref={setUnitRef('theme')} style={unitStyle('theme')}>
          <ThemeDropdown style={{ marginRight: '8px' }} />
        </span>

        <span ref={setUnitRef('zoom')} style={unitStyle('zoom')}>
          <Dropdown
            className={`zoom-dropdown ${sparkActive ? 'active' : ''}`}
            selection
            options={zoomOptions}
            value={dropdownValue}
            onChange={handleZoomChange}
            ref={zoomDisplayRef}
            icon={<Icon name='search' style={{ opacity: 0.7 }} />}
          />
        </span>

        {showNotes && (
          <span ref={setUnitRef('notes')} style={unitStyle('notes')}>
            <NotesToggle
              visible={visible}
              onToggle={handleNotesToggle}
              l={{ toggle_notes: 'Toggle Notes' }}
              aiVisible={aiVisible}
            />
            {show_ai_chat && <AIToggle
              visible={aiVisible}
              onToggle={handleAIToggle}
              l={{ toggle_ai: 'Toggle AI Assistant' }}
            />}
          </span>
        )}

        {show_help && (
          <span ref={setUnitRef('help')} style={unitStyle('help')}>
            <HelpMenu
              l={help_l}
              is_user_signed_in={is_user_signed_in}
              setTriedToSave={setTriedToSave}
            />
          </span>
        )}

        {visibleFolded.length > 0 && (
          <span ref={setTriggerRef} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <OverflowMenu
              folded={visibleFolded}
              panelOpen={!!panelOpen}
              zoomOptions={zoomOptions}
              zoomValue={dropdownValue}
              onZoomChange={handleZoomChange}
              notesVisible={visible}
              aiVisible={aiVisible}
              showAI={!!show_ai_chat}
              onNotesToggle={handleNotesToggle}
              onAIToggle={handleAIToggle}
              help_l={help_l}
              is_user_signed_in={is_user_signed_in}
              setTriedToSave={setTriedToSave}
              currentLanguage={currentLanguage}
            />
          </span>
        )}

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginRight: 0,
          paddingRight: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {(userMenuElement || showAuth) && (
              // With the globe folded, "Sign in" / the avatar ends the bar: keep it off the edge
              <span ref={setFixedRef} style={{ display: 'flex', alignItems: 'center', flexShrink: 0, paddingRight: isFolded('language') ? 8 : 0 }}>
                {userMenuElement}
                {showAuth && <SignInLink />}
              </span>
            )}
            {showAuth && (
              <span ref={setUnitRef('create')} style={unitStyle('create')}>
                <CreateAccountLink />
              </span>
            )}
          </div>
          <span ref={setUnitRef('language')} style={unitStyle('language')}>
            <LanguageSelector
              onLanguageChange={(language) => {
                window.open(language.link, '_self');
              }}
              currentLanguage={currentLanguage}
            />
          </span>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.user?.id === nextProps.user?.id &&
    prevProps.zoomValue === nextProps.zoomValue &&
    prevProps.visible === nextProps.visible &&
    prevProps.aiVisible === nextProps.aiVisible &&
    prevProps.action_name === nextProps.action_name &&
    prevProps.show_help === nextProps.show_help &&
    prevProps.is_user_signed_in === nextProps.is_user_signed_in &&
    prevProps.help_l === nextProps.help_l &&
    prevProps.show_ai_chat === nextProps.show_ai_chat &&
    prevProps.embed_mode === nextProps.embed_mode &&
    prevProps.onMinWidthChange === nextProps.onMinWidthChange &&
    JSON.stringify(prevProps.userMenu) === JSON.stringify(nextProps.userMenu)
  );
});

export default RightSection;